import { useState } from "react";

import { ListFilter } from "lucide-react";

import { GetAllFilesApiV1FilesGetData } from "@/api/generated/types.gen";
import { useAppForm } from "@/components/Form";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import {
  DOCUMENT_STATUS_OPTIONS,
  DOCUMENT_TYPES_OPTIONS,
  SORTING_ORDER_OPTIONS,
} from "../contants/documents";
import { FileFilterFormData } from "../schema/filters";

type FileFilterProps = {
  className?: string;
  onFiltersChange?: (filters: GetAllFilesApiV1FilesGetData["query"]) => void;
  isStatusEditable?: boolean;
};

export const FileFilter = ({
  className,
  onFiltersChange,
  isStatusEditable = true,
}: FileFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      classification: "",
      status: isStatusEditable ? "" : undefined,
      created_at: "",
      sort_by: "",
    } as FileFilterFormData,
    onSubmit: async ({ value }) => {
      const filterValues: GetAllFilesApiV1FilesGetData["query"] = {
        classification: value.classification || undefined,
        status: isStatusEditable ? value.status || undefined : undefined,
        created_at: value.created_at || undefined,
        sort_by:
          (value.sort_by as "recently_added" | "recently_updated" | "oldest") ||
          undefined,
      };

      // Call the callback to update filters in parent component
      onFiltersChange?.(filterValues);

      // Check if any filters are applied
      const activeFilters = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);
      setIsOpen(false);
    },
  });

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({
      classification: undefined,
      status: undefined,
      created_at: undefined,
      sort_by: undefined,
    });
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "gap-2 text-foreground transition-all duration-150",
              hasActiveFilters
                ? "border-primary bg-primary/10 hover:text-primary"
                : "border-dark-200 hover:border-primary",
              className
            )}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-96 space-y-1 rounded-xl border border-border bg-background-white px-4 py-4 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Filter</h3>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
              className="space-y-4"
            >
              <form.AppField name="classification">
                {(field) => (
                  <field.SelectField
                    label="Documentation Type"
                    placeholder="Select documentation type"
                    options={DOCUMENT_TYPES_OPTIONS}
                  />
                )}
              </form.AppField>
              {isStatusEditable && (
                <form.AppField name="status">
                  {(field) => (
                    <field.SelectField
                      label="Status"
                      placeholder="Select status"
                      options={DOCUMENT_STATUS_OPTIONS}
                    />
                  )}
                </form.AppField>
              )}

              <form.AppField name="created_at">
                {(field) => (
                  <field.DateField
                    label="Uploaded Date"
                    placeholder="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                )}
              </form.AppField>

              <form.AppField name="sort_by">
                {(field) => (
                  <field.SelectField
                    label="Sort By"
                    placeholder="Select sort order"
                    options={SORTING_ORDER_OPTIONS}
                  />
                )}
              </form.AppField>

              <form.AppForm>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                  >
                    Clear
                  </Button>
                  <form.SubmitButton size="sm">Apply Filters</form.SubmitButton>
                </div>
              </form.AppForm>
            </form>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
